import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';
import debounce from 'debounce';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { CmsImage, CmsLink, TeaserAspectRatio } from '../../types/global';
import { createAbsoutePathIfRelative } from '../../global/utility/tracking-helpers';

export interface UiCmsFullScreenTeaserBody {
  headline: string;
  text: string;
  date?: string;
  topic?: string;
  author?: string;
  textAlignment?: 'center' | 'left';
  link: CmsLink;
  image: CmsImage;
  mobileImage?: CmsImage;
  style: 'white' | 'neutral-100' | 'primary' | 'primary-100' | 'gradient-white' | 'gradient-dark';
}

@Component({
  tag: 'ui-cms-fullscreen-teaser',
  styleUrl: 'ui-cms-fullscreen-teaser.scss',
})
export class UiCmsFullscreenTeaser {
  @Element() el: HTMLElement;

  @Prop({ attribute: 'aspect-ratio' })
  aspectRatio: TeaserAspectRatio | string;

  @Prop({ mutable: true }) body: UiCmsFullScreenTeaserBody;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @State()
  private isMobile: boolean = false;

  @State()
  private isExpanded: boolean = false;

  @State()
  private teaserText: string = '';

  @State()
  private imageReference: CmsImage;

  private availableSizes = [240, 480, 640, 960, 1200, 2400];

  @State()
  private imageSize: number;

  private get formattedDate(): string {
    return this.body.date ? new Date(this.body.date).toLocaleDateString(document.documentElement.lang) : undefined;
  }

  private get isInverted(): boolean {
    return this.body.style === 'gradient-dark' || this.body.style === 'primary';
  }

  private get isTextCentered(): boolean {
    return this.body.textAlignment === 'center';
  }

  private get figureAspectRatio() {
    return { 'aspect-ratio': this.aspectRatio };
  }

  private linkRef: HTMLAnchorElement;

  private resizeObserver: ResizeObserver;

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    this.addResizeObserver();
    this.teaserText = this.body.text;
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.isMobile = window.innerWidth < 768;
        this.imageReference = this.isMobile ? this.body.mobileImage : this.body?.image;
        this.imageSize = this.availableSizes.find((size) => size > window.innerWidth);
      }, 100)
    );
    this.resizeObserver.observe(this.el);
  }

  private onTeaserMouseEnter() {
    if (this.isTextCentered && !this.isMobile) {
      this.teaserText = this.body.text;
      this.isExpanded = true;
    }
  }

  private onTeaserMouseLeave() {
    if (document.activeElement === this.linkRef) {
      return;
    }

    if (this.isTextCentered && !this.isMobile) {
      this.teaserText = '';
      this.isExpanded = false;
    }
  }

  private onChevronClick(event) {
    event.preventDefault();
    this.isExpanded = !this.isExpanded;
    this.teaserText = this.isExpanded ? this.body.text : '';
  }

  private generateTrackingElementLabelFromTeaserText(): string {
    const textSampleLength = 25 - this.body.headline.length;

    return textSampleLength < 1
      ? this.body.headline.substring(0, 25)
      : `${this.body.headline} ${this.body.text.substring(0, textSampleLength)}`;
  }

  private handleTracking(link: CmsLink): void {
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Teaser',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Teaser',
        elementLabel: link.title || this.generateTrackingElementLabelFromTeaserText(),
        target: createAbsoutePathIfRelative(link.src),
      },
      category: {
        area: 'Content',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);
  }

  render() {
    return (
      <Host>
        <a
          href={this.body.link?.src}
          target={this.body.link?.target}
          aria-label={this.body.link?.ariaLabel}
          ref={(el) => {
            this.linkRef = el;
          }}
          onFocus={this.onTeaserMouseEnter.bind(this)}
          onBlur={this.onTeaserMouseLeave.bind(this)}
          onClick={() => this.handleTracking(this.body.link)}
        >
          <sd-teaser-media
            variant={this.body.style}
            class={{
              'fullscreen-teaser--align-center': this.isTextCentered,
              'fullscreen-teaser--expandable-visible': this.isExpanded,
              'fullscreen-teaser--aspect-ratio-auto': this.aspectRatio === 'auto',
            }}
            onMouseEnter={this.onTeaserMouseEnter.bind(this)}
            onMouseLeave={this.onTeaserMouseLeave.bind(this)}
          >
            <span slot="meta" class={{ 'sd-meta sd-meta--size-sm': true, 'sd-meta--inverted': this.isInverted }}>
              {this.body.date && (
                <span class={{ 'sd-meta--pipe': !!(this.body.author || this.body.topic) }}>{this.formattedDate}</span>
              )}
              {this.body.author && <span class={{ 'sd-meta--pipe': !!this.body.topic }}>{this.body.author}</span>}
              {this.body.topic && <span>{this.body.topic}</span>}
            </span>
            <h2 slot="headline">{this.body.headline}</h2>
            <div
              slot={this.isTextCentered && !this.isMobile ? 'expandable' : undefined}
              class={{
                'fullscreen-teaser__text sd-prose sd-prose--full-width': true,
                'sd-prose--inverted': this.isInverted,
              }}
              innerHTML={this.teaserText}
            ></div>
            <div class="fullscreen-teaser__chevron-wrapper">
              {this.body.text && this.isTextCentered && !this.isMobile && (
                <sd-icon
                  class={{
                    'fullscreen-teaser__chevron': true,
                    'fullscreen-teaser__chevron--opened': this.isExpanded,
                  }}
                  library={window.ui.cmsState.iconLibrary}
                  name="union-investment/system/chevron-bottom"
                  color={this.isInverted ? 'white' : 'primary'}
                  onClick={this.onChevronClick.bind(this)}
                ></sd-icon>
              )}
              {!this.isTextCentered && (
                <ui-cms-link body={this.body.link} inverted={this.isInverted} disableTracking></ui-cms-link>
              )}
            </div>
            {this.imageReference?.copyright && (
              <p
                class={{
                  'sd-meta sd-meta--size-sm': true,
                  'sd-meta--inverted': this.isInverted,
                }}
              >
                {`© ${this.imageReference?.copyright}`}
              </p>
            )}
            {this.imageReference && (
              <figure slot="media" style={{ ...this.figureAspectRatio }}>
                <ui-cms-image
                  class="fullscreen-teaser__image"
                  objectPosition={this.imageReference?.objectPosition}
                  src={this.imageReference?.src}
                  alt={this.imageReference?.alt}
                  size={this.imageSize}
                />
              </figure>
            )}
          </sd-teaser-media>
        </a>
      </Host>
    );
  }
}
